import React, { useState } from 'react';
import { Button, Flex, LoadingOverlay, Text, TextInput } from '@mantine/core';
import { ILinkedEmail } from '@/entities/admin-app/email';
import { useTranslation } from 'react-i18next';
import { showNotification } from '@mantine/notifications';
import { IUsePopUpUI } from '@components/UI/PopUp';
import { useLazyChangeEmailLoginQuery } from '@/entities/admin-app/email/api';
import { useMediaQuery } from '@mantine/hooks';

interface IRenameEmailModalProps extends IUsePopUpUI {
  selectedEmail: ILinkedEmail | null;
  onSuccess?: () => void;
}

export const RenameMail = ({ selectedEmail, setIsOpen, onSuccess }: IRenameEmailModalProps) => {
  const { t } = useTranslation();
  const [newLogin, setNewLogin] = useState(selectedEmail?.Login || selectedEmail?.Email || '');
  const [changeEmailLogin, { isLoading }] = useLazyChangeEmailLoginQuery();

  const modalClose = () => setIsOpen(false);

  // Валидация логина
  const validateLogin = (login: string): string | null => {
    if (!login || !login.trim()) {
      return t('validation.emptyInput');
    }
    if (/\s/.test(login)) {
      return t('validation.loginWithoutSpaces');
    }
    if (login.length < 3) {
      return t('validation.minLengthSymbolsWithCount.symbols_other', { count: 3 });
    }
    if (login.length > 50) {
      return t('validation.maxLengthSymbolsWithCount.symbols_other', { count: 50 });
    }
    return null;
  };

  const handleRenameEmail = async () => {
    // Проверяем, можно ли переименовать
    if (!selectedEmail?.IsCanRename) {
      showNotification({
        title: t('errors.noRights'),
        message: '',
        autoClose: true,
        color: 'red',
        style: { zIndex: 1003 }
      });
      return;
    }

    // Валидация
    const validationError = validateLogin(newLogin);
    if (validationError) {
      showNotification({
        title: validationError,
        message: '',
        autoClose: true,
        color: 'red',
        style: { zIndex: 1003 }
      });
      return;
    }

    if (!selectedEmail?.Id) {
      showNotification({
        title: t('errors.somethingWentWrong'),
        message: '',
        autoClose: true,
        color: 'red',
        style: { zIndex: 1003 }
      });
      return;
    }

    try {
      const result = await changeEmailLogin({
        id: selectedEmail.Id,
        newEmail: newLogin.trim()
      });

      if ('error' in result) {
        showNotification({
          title: t('errors.somethingWentWrong'),
          message: '',
          autoClose: true,
          color: 'red',
          style: { zIndex: 1003 }
        });
      } else {
        showNotification({
          title: t('profile.renameEmailSuccess'),
          message: '',
          autoClose: true,
          color: 'green',
          style: { zIndex: 1003 }
        });

        if (onSuccess) {
          onSuccess();
        }

        modalClose();
      }
    } catch (err) {
      console.error('Ошибка переименования email', err);
      showNotification({
        title: t('errors.somethingWentWrong'),
        message: '',
        autoClose: true,
        color: 'red',
        style: { zIndex: 1003 }
      });
    }
  };

  const hasSpaces = /\s/.test(newLogin);
  const isTooShort = newLogin.trim().length < 3;
  const isTooLong = newLogin.trim().length > 50;
  const hasValidationError = hasSpaces || isTooShort || isTooLong;
  const isMobile = useMediaQuery('(max-width: 768px)');

  return (
    <Flex direction="column" gap={12} p={12} w={isMobile ? '100%' : 300} pos="relative">
      <Text fz="md">{t('profile.renameEmailTitle')}</Text>
      <TextInput
        placeholder={t('profile.renameEmailPlaceholder')}
        label={t('profile.renameEmailLabel')}
        withAsterisk
        value={newLogin}
        onChange={(e) => setNewLogin(e.target.value)}
        disabled={isLoading || !selectedEmail?.IsCanRename}
        error={
          hasSpaces
            ? t('validation.loginWithoutSpaces')
            : isTooShort
            ? t('validation.minLengthSymbolsWithCount.symbols_other', { count: 3 })
            : isTooLong
            ? t('validation.maxLengthSymbolsWithCount.symbols_other', { count: 50 })
            : undefined
        }
      />
      {!selectedEmail?.IsCanRename && (
        <Text size="xs" c="red">
          {t('errors.noRights')}
        </Text>
      )}
      <Flex justify="space-between" gap={12}>
        <Button
          fullWidth
          variant="filled"
          onClick={handleRenameEmail}
          disabled={
            !newLogin.trim() || isLoading || hasValidationError || !selectedEmail?.IsCanRename
          }
        >
          {t('save')}
        </Button>
        <Button fullWidth color="gray" variant="outline" onClick={modalClose} disabled={isLoading}>
          {t('profile.cancel')}
        </Button>
      </Flex>
      <LoadingOverlay visible={isLoading} overlayProps={{ blur: 2 }} />
    </Flex>
  );
};
